import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useUpdateBudgetApi } from '../api/use-update-budget';
// Collapse Form Schema
export const collapseFormSchema = z.object({
    collapsedCategoryIds: z.array(z.string().uuid()),
});
// Collapse Form Hook
export const useBudgetCollapsedCategoriesForm = ({ budget, categories, onSuccess }) => {
    const { mutate: updateBudget, isPending: isUpdatePending } = useUpdateBudgetApi({ onSuccess });
    // defaultValues now include logic to collapse all by default when no saved values
    const defaultValues = useMemo(() => toFormData(budget, categories), [budget, categories]);
    const form = useForm({
        resolver: zodResolver(collapseFormSchema),
        defaultValues,
    });
    // Reset form whenever budget or categories change
    useEffect(() => {
        form.reset(defaultValues);
    }, [budget, categories, form, defaultValues]);
    /**
     * Handles the form submission event, building the API payload and
     * invoking the update budget mutation with the new collapsed category IDs.
     *
     * Applies normalization logic: if all categories are collapsed, treats it as
     * no categories collapsed (empty array) to maintain binary state behavior.
     *
     * @param values - Sanitized collapse form values containing the collapsed category IDs.
     */
    const submitForm = useCallback((values) => {
        if (!budget) {
            return;
        }
        // normalize collapsed category IDs - if all categories are collapsed, treat as none
        const normalizedCollapsedIds = normalizeCollapsedCategoryIds(values.collapsedCategoryIds, categories);
        const payload = {
            ...budget,
            settings: {
                ...budget.settings,
                collapsedCategoryIds: normalizedCollapsedIds,
            },
        };
        updateBudget(payload);
    }, [budget, categories, updateBudget]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isUpdatePending,
    };
};
/**
 * Converts the budget's current collapsed category IDs into form data.
 *
 * Applies denormalization logic:
 * - If stored collapsed IDs is empty and there are categories,
 *   defaults to collapsing all categories by returning all IDs.
 * - Otherwise, preserves stored state as-is for form initialization.
 *
 * @param budget - The current budget object containing settings
 * @param categories - The complete array of available categories
 * @returns A CollapseForm object with the initialized collapsed category IDs
 */
function toFormData(budget, categories) {
    const storedIds = budget?.settings?.collapsedCategoryIds ?? [];
    // if no saved IDs but we have categories, collapse all by default
    if (storedIds.length === 0 && categories.length > 0) {
        return { collapsedCategoryIds: categories.map((cat) => cat.id) };
    }
    return { collapsedCategoryIds: storedIds };
}
/**
 * Normalizes the collapsed category IDs by converting edge cases to standard states.
 *
 * Business logic:
 * - If all categories are collapsed → treat as none collapsed (empty array)
 * - If no categories are collapsed → keep as empty array
 * - Otherwise → keep the partial selection as-is
 *
 * This ensures we only store intermediate states, never "all collapsed" state.
 *
 * @param collapsedIds - The array of collapsed category IDs from the form
 * @param categories - The complete array of available categories
 * @returns Normalized array of collapsed category IDs
 */
function normalizeCollapsedCategoryIds(collapsedIds, categories) {
    const allCategoryIds = categories.map((cat) => cat.id);
    // if every category is collapsed, treat it as no categories collapsed
    if (collapsedIds.length === allCategoryIds.length && allCategoryIds.every((id) => collapsedIds.includes(id))) {
        return [];
    }
    // otherwise, return the collapsed IDs as-is (including empty array for none collapsed)
    return collapsedIds;
}
